// crewbit-admin/src/components/money/proposals/ProposalSuggestionBanner.tsx
//
// Tier 2-UI Phase B Phase B (admin) — display-only banner that surfaces a
// ProposedCategorization envelope above the category picker in
// MoneyExpenseLogPanel. Per Decision §T2UI.PhaseB-admin.D1: no apply-to-picker
// action — operator reads the suggestion + reasoning and picks from the
// existing EXPENSE_CATEGORY_GROUPS picker independently. Save path stays via
// useExpenseDraft.save() per D2.
//
// Composes ProposalConfidenceMeter + ProposalPill + ProposalReasoningTooltip
// (the 3 primitives shipped in Phase B Commit 1). Extracted from
// MoneyExpenseLogPanel to keep that file under Hard Rule #14's 500-line cap.

import { useState } from 'react';
import { palette, radii, spacing } from '../../../styles/tokens';
import type { ProposedCategorization } from '../../../types/proposals';
import { PROPOSAL_CATEGORY_LABELS } from '../../../lib/proposalCategoryLabels';
import ProposalPill, { type ProposalPillSource } from './ProposalPill';
import ProposalReasoningTooltip from './ProposalReasoningTooltip';
import ProposalConfidenceMeter from './ProposalConfidenceMeter';

interface Props {
  proposal: ProposedCategorization | null;
  proposing: boolean;
  proposalError: string | null;
}

export default function ProposalSuggestionBanner({
  proposal,
  proposing,
  proposalError,
}: Props) {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!proposing && !proposal && !proposalError) return null;

  const pillSource: ProposalPillSource = proposal?.reasoning.source ?? 'ai_fallback';
  const reasoningText = proposal ? buildReasoningText(proposal) : '';

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.xs,
          padding: spacing.sm + 2,
          background: palette.g50,
          borderRadius: radii.sm,
          border: `1px solid ${palette.g100}`,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: palette.g600 }}>
          Suggested categorization
        </div>
        {proposing ? (
          <div style={{ fontSize: 12.5, color: palette.g600 }}>
            Generating suggestion…
          </div>
        ) : null}
        {!proposing && proposal ? (
          <>
            <ProposalConfidenceMeter confidence={proposal.confidence} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xs,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>
                {PROPOSAL_CATEGORY_LABELS[proposal.category]}
              </span>
              <ProposalPill source={pillSource} onClick={() => setTooltipOpen(true)} />
            </div>
            {proposal.is_capital ? (
              <div style={{ fontSize: 12, color: palette.g600 }}>
                Capital purchase
                {proposal.cca_class !== null ? ` — Class ${proposal.cca_class}` : ''}
              </div>
            ) : null}
            {proposal.deductible_percentage !== null && proposal.deductible_percentage !== 100 ? (
              <div style={{ fontSize: 12, color: palette.g600 }}>
                Deductible at {proposal.deductible_percentage}%
              </div>
            ) : null}
          </>
        ) : null}
        {!proposing && proposalError ? (
          <div style={{ fontSize: 12.5, color: palette.amber }}>{proposalError}</div>
        ) : null}
      </div>

      {proposal ? (
        <ProposalReasoningTooltip
          visible={tooltipOpen}
          reasoning={reasoningText}
          onDismiss={() => setTooltipOpen(false)}
        />
      ) : null}
    </>
  );
}

function buildReasoningText(proposed: ProposedCategorization): string {
  const r = proposed.reasoning;
  if (r.source === 'rule') {
    return r.matched_pattern
      ? `Matched the "${r.matched_pattern}" rule.`
      : 'Matched a categorization rule.';
  }
  if (r.ai_signal) return `AI suggestion based on: ${r.ai_signal}.`;
  if (r.category_guess) return `AI guessed: ${r.category_guess}.`;
  return 'AI suggestion — no rule match.';
}
